/**
 * Trello 546 Phase 2 — force every semi-prod landing page onto the xt-aware
 * checkout lane (link-first wallet, live balance check, orchestrator-gated
 * charge) instead of relying on each funnel to forward `?xt=1` by hand.
 *
 * shouldForceXtLane() reuses the already-live DEPLOY_TIER build flag:
 * next.config.js's envGuard() sets/validates it per box (staging /
 * semi_production / production), and its ALLOWED_ENV_KEYS allowlist already
 * inlines DEPLOY_TIER into the CLIENT bundle (next.config.js ~line 101) —
 * so this needs zero new CI/env wiring and zero risk of a second flag
 * drifting out of sync with it. Production boxes never set DEPLOY_TIER to
 * "semi_production" (prod-web/prod-fe leave it unset), so every call site
 * that gates on this flag stays byte-identical in production.
 */
export const shouldForceXtLane = () => process.env.DEPLOY_TIER === "semi_production";

/**
 * Owner decision (Trello 546, 2026-10-08): SINGLE_PURCHASE / one-time
 * packages stay on the legacy checkout path this round, not the xt lane.
 *
 * Mirrors the `type !== "subscription"` idiom already used on this exact
 * package shape in helpers/checkoutRecurringTerms.js and
 * pages/payment/purchases/[id].js (backend
 * constant: App\Models\ProductPackage::SUBSCRIPTION = 'subscription',
 * SINGLE_PURCHASE = 'single-purchase'). A package with no `type` at all is
 * treated as eligible (not single-purchase) to match those existing call
 * sites' default-open behaviour.
 *
 * Only call this where the package object is already in hand (final-summary's
 * selected plan, collab/package-list's plan item) — it never fetches. Call
 * sites that only ever see a package id/query string (price-login.js,
 * helpers/paymentRedirect.js) can't know this cheaply and instead force
 * unconditionally, relying on the backend's own ineligible-package fallback.
 */
export const isSinglePurchasePackage = (packageItem) => !!(
  packageItem
  && packageItem.type
  && String(packageItem.type).toLowerCase() !== "subscription"
);
